import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { 
  SearchIcon,
  BookOpenIcon,
  ArrowRightIcon,
  FileTextIcon,
  PlayIcon,
  HelpCircleIcon,
  SettingsIcon,
  CreditCardIcon,
  UsersIcon,
  BarChart3Icon,
  ShieldIcon,
  ExternalLinkIcon,
  ChevronRightIcon,
  StarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon
} from 'lucide-react';

const Documentation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showArticleModal, setShowArticleModal] = useState(false);

  // Documentation categories and articles
  const categories = [
    { id: 'all', name: 'Tümü', icon: BookOpenIcon },
    { id: 'getting-started', name: 'Başlangıç', icon: PlayIcon },
    { id: 'advance-management', name: 'Avans Yönetimi', icon: CreditCardIcon },
    { id: 'user-guide', name: 'Kullanıcı Rehberi', icon: UsersIcon },
    { id: 'admin-guide', name: 'Yönetici Rehberi', icon: SettingsIcon },
    { id: 'reports', name: 'Raporlama', icon: BarChart3Icon },
    { id: 'security', name: 'Güvenlik', icon: ShieldIcon },
    { id: 'faqs', name: 'Sık Sorulanlar', icon: HelpCircleIcon }
  ];

  const articles = [
    // Getting Started
    {
      id: 1,
      title: 'Hızlı Başlangıç Rehberi',
      description: 'Advance Cash\'a giriş yapın ve ilk avans talebinizi oluşturun.',
      category: 'getting-started',
      readTime: '5 dk',
      difficulty: 'Başlangıç',
      popular: true,
      content: `
# Hızlı Başlangıç Rehberi

Advance Cash'a hoş geldiniz! Bu rehber, platformda ilk adımlarınızı atmanız için tasarlanmıştır.

## 1. Giriş Yapma
- Demo hesabı: demo@quattroflow.com / NewPass123!
- Şirket hesabınızla giriş yapın

## 2. Dashboard'u Keşfetme
- Ana menüden farklı bölümlere erişin
- İstatistikleri inceleyin
- Aktif taleplerini kontrol edin

## 3. İlk Avans Talebinizi Oluşturun
1. "Yeni Talep" menüsüne tıklayın
2. Gerekli bilgileri doldurun
3. Talep tutarını ve para birimini seçin
4. Kapatma süresini belirleyin

## 4. Talep Sürecini Takip Edin
- "Tüm Talepler" bölümünden durumu kontrol edin
- Onay bekleyen taleplerinizi görüntüleyin
      `
    },
    {
      id: 2,
      title: 'Sistem Gereksinimleri',
      description: 'Advance Cash\'u kullanmak için gerekli teknik şartlar.',
      category: 'getting-started',
      readTime: '3 dk',
      difficulty: 'Başlangıç',
      content: `
# Sistem Gereksinimleri

## Desteklenen Tarayıcılar
- Chrome 90+
- Firefox 88+
- Safari 14+
- Edge 90+

## Önerilen Sistem
- 4GB RAM
- Stabil internet bağlantısı
- 1366x768 minimum çözünürlük
      `
    },
    // Advance Management
    {
      id: 3,
      title: 'Avans Talebi Oluşturma',
      description: 'Detaylı avans talebi oluşturma süreci ve ipuçları.',
      category: 'advance-management',
      readTime: '8 dk',
      difficulty: 'Orta',
      popular: true,
      content: `
# Avans Talebi Oluşturma

## Talep Formu Alanları
- **Tutar**: İhtiyacınız olan miktar
- **Para Birimi**: TRY, USD, EUR seçenekleri
- **Kategori**: Proje, seyahat, ofis giderleri
- **Açıklama**: Detaylı açıklama yazın

## İpuçları
- Gerçekçi tutarlar belirleyin
- Açıklamalarda spesifik olun
- Uygun kapatma süresini seçin
      `
    },
    {
      id: 4,
      title: 'Avans Kapatma Süreci',
      description: 'Avansınızı nasıl kapatacağınızı öğrenin.',
      category: 'advance-management',
      readTime: '10 dk',
      difficulty: 'Orta',
      content: `
# Avans Kapatma Süreci

## Kapatma Adımları
1. "Avans Kapama" menüsüne gidin
2. Kapatmak istediğiniz avansı seçin
3. Harcama kalemlerini ekleyin
4. Fişleri/faturaları yükleyin
5. "Kapat" butonuna tıklayın

## Gerekli Belgeler
- Faturalar
- Makbuzlar
- Harcama fişleri
      `
    },
    // User Guide
    {
      id: 5,
      title: 'Dashboard Kullanımı',
      description: 'Ana dashboard\'un tüm özelliklerini keşfedin.',
      category: 'user-guide',
      readTime: '6 dk',
      difficulty: 'Başlangıç',
      content: `
# Dashboard Kullanımı

## Ana Bölümler
- **İstatistikler**: Genel durumunuzu görün
- **Hızlı İşlemler**: Sık kullanılan fonksiyonlar
- **Son Aktiviteler**: Güncel hareketler

## Navigasyon
- Sol menüden bölümlere erişin
- Arama fonksiyonunu kullanın
- Bildirimlerinizi kontrol edin
      `
    },
    // Admin Guide
    {
      id: 6,
      title: 'Kullanıcı Yönetimi',
      description: 'Yeni kullanıcı ekleme ve rol atama.',
      category: 'admin-guide',
      readTime: '12 dk',
      difficulty: 'İleri',
      content: `
# Kullanıcı Yönetimi

## Yeni Kullanıcı Ekleme
1. "Ayarlar" > "Kullanıcı Yönetimi"
2. "Yeni Kullanıcı" butonuna tıklayın
3. E-posta ve rol bilgilerini girin
4. Davet gönderin

## Roller ve Yetkiler
- **Super Admin**: Tüm yetkiler
- **Admin**: Şirket yönetimi
- **Muhasebe**: Mali işlemler
- **Kullanıcı**: Temel işlemler
      `
    },
    // FAQs
    {
      id: 7,
      title: 'Sık Sorulan Sorular',
      description: 'En çok merak edilen konularda yanıtlar.',
      category: 'faqs',
      readTime: '15 dk',
      difficulty: 'Başlangıç',
      popular: true,
      content: `
# Sık Sorulan Sorular

## Genel Sorular

### Q: Avans talebi ne kadar sürede onaylanır?
A: Genellikle 1-2 iş günü içinde onaylanır.

### Q: Hangi para birimlerini kullanabilirim?
A: TRY, USD ve EUR desteklenmektedir.

### Q: Avans kapatma süresi ne kadar?
A: 15, 30 veya 60 gün arasından seçebilirsiniz.

## Teknik Sorular

### Q: Şifremi unuttum, ne yapmalıyım?
A: Giriş sayfasında "Şifremi Unuttum" linkini kullanın.

### Q: Mobil uygulaması var mı?
A: Şu anda web tabanlı, mobil uyumlu arayüz mevcuttur.
      `
    },
    // Reports
    {
      id: 8,
      title: 'Raporlama Sistemi',
      description: 'Avans raporlarını nasıl oluşturacağınızı öğrenin.',
      category: 'reports',
      readTime: '8 dk',
      difficulty: 'Orta',
      content: `
# Raporlama Sistemi

## Mevcut Raporlar
- Cari Hesap Ekstresi
- Harcama Raporu
- Avans Özet Raporu

## Rapor Oluşturma
1. İlgili bölüme gidin
2. Filtreleri ayarlayın
3. "Rapor Oluştur" butonuna tıklayın
4. PDF olarak indirin
      `
    }
  ];

  // Filter articles based on search and category
  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Get difficulty badge styling
  const getDifficultyBadge = (difficulty) => {
    const config = {
      'Başlangıç': { className: 'bg-green-100 text-green-800' },
      'Orta': { className: 'bg-blue-100 text-blue-800' },
      'İleri': { className: 'bg-orange-100 text-orange-800' }
    };
    
    return config[difficulty] || config['Başlangıç'];
  };

  // View article
  const viewArticle = (article) => {
    setSelectedArticle(article);
    setShowArticleModal(true);
  };

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        document.querySelector('input[placeholder*="Ctrl+K"]')?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold text-gray-900">
              Advance Cash Dokümantasyon
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Avans yönetimi sisteminizden en iyi şekilde yararlanmak için gereksinim duyduğunuz 
              tüm rehberler, kılavuzlar ve ipuçları burada.
            </p>
            
            {/* Search */}
            <div className="max-w-2xl mx-auto mt-8">
              <div className="relative">
                <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Dokümanlarda ara... (Ctrl+K)"
                  className="pl-12 pr-4 py-3 text-lg border-2 border-gray-300 focus:border-teal-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Kategoriler</h3>
                <div className="space-y-2">
                  {categories.map((category) => {
                    const Icon = category.icon;
                    const isActive = selectedCategory === category.id;
                    const categoryCount = category.id === 'all' 
                      ? articles.length 
                      : articles.filter(a => a.category === category.id).length;
                    
                    return (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                          isActive 
                            ? 'bg-teal-100 text-teal-900 border-teal-200' 
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon className="w-5 h-5" />
                          <span className="font-medium">{category.name}</span>
                        </div>
                        <Badge className="bg-gray-100 text-gray-600 text-xs">
                          {categoryCount}
                        </Badge>
                      </button>
                    );
                  })}
                </div>
              </Card>

              {/* Quick Links */}
              <Card className="p-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Hızlı Bağlantılar</h3>
                <div className="space-y-3">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start" 
                    onClick={() => navigate('/dashboard/help')}
                  >
                    <HelpCircleIcon className="w-4 h-4 mr-2" />
                    Destek Merkezi
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => window.open('https://discord.gg/quattroflow', '_blank')}
                  >
                    <ExternalLinkIcon className="w-4 h-4 mr-2" />
                    Discord Topluluğu
                  </Button>
                </div>
              </Card>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Get Started Section */}
            {selectedCategory === 'all' && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <PlayIcon className="w-6 h-6 mr-2 text-teal-600" />
                  Hemen Başlayın
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => viewArticle(articles[0])}>
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                        <PlayIcon className="w-6 h-6 text-teal-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-teal-600">
                          Hızlı Başlangıç Rehberi
                        </h3>
                        <p className="text-gray-600 text-sm mb-3">
                          Platform turunu yapın ve başarılı olmak için ipuçlarını öğrenin.
                        </p>
                        <div className="flex items-center text-teal-600 text-sm font-medium">
                          <span>Başla</span>
                          <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => viewArticle(articles.find(a => a.category === 'faqs'))}>
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <HelpCircleIcon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600">
                          Sık Sorulan Sorular
                        </h3>
                        <p className="text-gray-600 text-sm mb-3">
                          Platformdan en iyi şekilde yararlanmak için net ve faydalı yanıtlar bulun.
                        </p>
                        <div className="flex items-center text-blue-600 text-sm font-medium">
                          <span>İncele</span>
                          <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            )}

            {/* Search Results Info */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {searchTerm ? `"${searchTerm}" için sonuçlar` : 
                   selectedCategory === 'all' ? 'Tüm Dokümanlar' : 
                   categories.find(c => c.id === selectedCategory)?.name}
                </h2>
                <p className="text-gray-600 text-sm">
                  {filteredArticles.length} doküman bulundu
                </p>
              </div>
            </div>

            {/* Articles Grid */}
            {filteredArticles.length === 0 ? (
              <Card className="p-12 text-center">
                <FileTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Doküman bulunamadı
                </h3>
                <p className="text-gray-600">
                  Farklı bir arama terimi deneyebilir veya kategori değiştirebilirsiniz.
                </p>
              </Card>
            ) : (
              <div className="space-y-6">
                {filteredArticles.map((article) => (
                  <Card key={article.id} className="p-6 hover:shadow-lg transition-all cursor-pointer group" onClick={() => viewArticle(article)}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-teal-600">
                            {article.title}
                          </h3>
                          {article.popular && (
                            <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                              <StarIcon className="w-3 h-3 mr-1" />
                              Popüler
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-gray-600 mb-4">{article.description}</p>
                        
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center text-gray-500">
                            <ClockIcon className="w-4 h-4 mr-1" />
                            {article.readTime}
                          </div>
                          <Badge className={getDifficultyBadge(article.difficulty).className}>
                            {article.difficulty}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="ml-6">
                        <Button 
                          size="sm"
                          className="group-hover:bg-teal-600 group-hover:text-white transition-colors"
                          onClick={() => viewArticle(article)}
                        >
                          <span>Oku</span>
                          <ChevronRightIcon className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Support Section */}
            <Card className="p-8 mt-12 bg-gradient-to-r from-teal-50 to-blue-50 border-teal-200">
              <div className="text-center">
                <HelpCircleIcon className="w-12 h-12 text-teal-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Aradığınızı bulamadınız mı?
                </h3>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                  Kişiselleştirilmiş yardım almak için destek talebi oluşturun. 
                  Ekibimiz size en kısa sürede yardımcı olmak için burada.
                </p>
                <Button 
                  size="lg"
                  className="bg-teal-600 hover:bg-teal-700"
                  onClick={() => navigate('/dashboard/help')}
                >
                  <HelpCircleIcon className="w-5 h-5 mr-2" />
                  Destek Talebi Oluştur
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Article Detail Modal */}
      <Dialog open={showArticleModal} onOpenChange={setShowArticleModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            {selectedArticle && (
              <>
                <DialogTitle className="text-2xl font-bold text-gray-900 pr-8">
                  {selectedArticle.title}
                </DialogTitle>
                <div className="flex items-center space-x-4 mt-2">
                  <div className="flex items-center text-gray-500 text-sm">
                    <ClockIcon className="w-4 h-4 mr-1" />
                    {selectedArticle.readTime}
                  </div>
                  <Badge className={getDifficultyBadge(selectedArticle.difficulty).className}>
                    {selectedArticle.difficulty}
                  </Badge>
                  {selectedArticle.popular && (
                    <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                      <StarIcon className="w-3 h-3 mr-1" />
                      Popüler
                    </Badge>
                  )}
                </div>
              </>
            )}
          </DialogHeader>
          
          {selectedArticle && (
            <div className="py-6">
              {/* Article Description */}
              <p className="text-lg text-gray-600 mb-6 p-4 bg-gray-50 rounded-lg border-l-4 border-teal-500">
                {selectedArticle.description}
              </p>

              {/* Article Content */}
              <div className="prose prose-lg max-w-none">
                <div className="bg-white">
                  {selectedArticle.content.split('\n').map((line, index) => {
                    if (line.startsWith('# ')) {
                      return <h1 key={index} className="text-3xl font-bold text-gray-900 mt-8 mb-4">{line.substring(2)}</h1>;
                    } else if (line.startsWith('## ')) {
                      return <h2 key={index} className="text-2xl font-semibold text-gray-800 mt-6 mb-3">{line.substring(3)}</h2>;
                    } else if (line.startsWith('### ')) {
                      return <h3 key={index} className="text-xl font-medium text-gray-700 mt-4 mb-2">{line.substring(4)}</h3>;
                    } else if (line.startsWith('- ')) {
                      return <li key={index} className="text-gray-600 ml-6 mb-1">{line.substring(2)}</li>;
                    } else if (line.match(/^\d+\./)) {
                      return <li key={index} className="text-gray-600 ml-6 mb-1 list-decimal">{line.substring(line.indexOf('.') + 1).trim()}</li>;
                    } else if (line.startsWith('**') && line.endsWith('**')) {
                      return <p key={index} className="text-gray-800 font-semibold mb-2">{line.substring(2, line.length - 2)}</p>;
                    } else if (line.trim() === '') {
                      return <br key={index} />;
                    } else {
                      return <p key={index} className="text-gray-600 mb-3 leading-relaxed">{line}</p>;
                    }
                  })}
                </div>
              </div>

              {/* Helpful Section */}
              <div className="mt-8 p-6 bg-gray-50 rounded-lg border">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Bu sayfa yardımcı oldu mu?</h4>
                <div className="flex space-x-3">
                  <Button size="sm" variant="outline" className="flex items-center">
                    <CheckCircleIcon className="w-4 h-4 mr-2 text-green-600" />
                    Evet
                  </Button>
                  <Button size="sm" variant="outline" className="flex items-center">
                    <XCircleIcon className="w-4 h-4 mr-2 text-red-600" />
                    Hayır
                  </Button>
                </div>
              </div>

              {/* Related Articles */}
              <div className="mt-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">İlgili Dokümanlar</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {articles
                    .filter(a => a.category === selectedArticle?.category && a.id !== selectedArticle?.id)
                    .slice(0, 2)
                    .map(relatedArticle => (
                      <Card key={relatedArticle.id} className="p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => viewArticle(relatedArticle)}>
                        <h5 className="font-medium text-gray-900 mb-2">{relatedArticle.title}</h5>
                        <p className="text-gray-600 text-sm mb-2">{relatedArticle.description}</p>
                        <div className="flex items-center text-xs text-gray-500">
                          <ClockIcon className="w-3 h-3 mr-1" />
                          {relatedArticle.readTime}
                        </div>
                      </Card>
                    ))
                  }
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowArticleModal(false)}
            >
              Kapat
            </Button>
            <Button 
              onClick={() => {
                setShowArticleModal(false);
                navigate('/dashboard/help');
              }}
            >
              <HelpCircleIcon className="w-4 h-4 mr-2" />
              Yardım İste
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Documentation;